<template>
    <div class="my-2 pt-2 pb-3 flex justify-between items-center border-b">
        <!-- ANFORDERUNGEN-V2 V2-04: Links steht, was das Projekt BESCHREIBT,
             rechts, was man TUN kann. -->
        <div class="flex" data-test="infobar-info">
            <p class="flex items-center text-sm text-gray-500">
                <span
                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                    :class="project.style"
                >
                    {{ $t("states." + project.state) }}
                </span>
            </p>
            <p class="flex items-center text-sm ml-6 text-gray-500">
                <FolderIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                {{ apartments.length }}
                <span class="hidden md:flex ml-1">{{ $t("projects.apartments") }}</span>
            </p>
            <!-- V2-04: "Karte anzeigen" verändert nichts, sondern zeigt die
                 Lage des Projekts — es gehört damit zur beschreibenden Hälfte.
                 Verhalten unverändert: öffnet weiterhin in einem neuen Tab. -->
            <a
                :href="mapsLink"
                target="_blank"
                class="flex items-center text-sm text-gray-500 ml-6"
            >
                <LocationMarkerIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                <span class="hidden md:flex ml-1">
                    {{ $t("projects.card") }}
                </span>
            </a>
            <!-- <p class="flex items-center text-sm text-gray-500 ml-6">
                <ExclamationIcon class="mr-2 h-5 w-5 text-red-700" />
                {{missedDeadlines}} <span class="hidden md:flex ml-1">{{ $t("projects.deadline") }}</span></p> -->
        </div>
        <!-- V2-04, Reihenfolge der Aktionen: Löschen · Status ändern · Export ·
             Dokument verteilen · Spalten verwalten · Vorlage. "Vorlage" steht
             ausdrücklich ganz aussen rechts. Am Verhalten der einzelnen
             Einträge ändert sich nichts. -->
        <div class="flex" data-test="infobar-actions">
            <button
                type="button"
                class="flex items-center text-sm text-gray-500 ml-6"
                v-if="deletable"
                @click="deleteProject"
            >
                <TrashIcon
                    class="mr-2 h-5 w-5 text-red-700 text-minplan-darkbeige hover:text-minplan-darkgreybeige"
                />
                {{ $t("actions.delete") }}
            </button>

            <button
                v-if="editProjectStateRoute"
                type="button"
                class="flex items-center text-sm text-gray-500 ml-6"
                @click="editProjectState"
            >
                <TagIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                <span class="hidden md:flex ml-1">
                    {{ $t("projects.stateChange") }}
                </span>
            </button>

            <!-- Welle 3, Thema 6 (Spec docs/superpowers/specs/2026-08-04-welle3-thema6-exportmenue-design.md):
                 Export-Menü — XLSX-Downloads, echte <a href> (kein Inertia-XHR).
                 Welle 3, Thema 5 (Spec
                 docs/superpowers/specs/2026-08-04-welle3-thema5-excel-abrechnung-design.md):
                 dritter Eintrag (Abrechnung, Sammel-Export) — begründeter Umbau von
                 `v-if="deadlinesExportLink && buyersExportLink"` auf eine Einzelprüfung je
                 Eintrag: das Dropdown zeigt, was tatsächlich vorhanden ist, statt alle
                 Links an eine gemeinsame UND-Bedingung zu koppeln.
                 Welle 3, Thema 12 (Spec
                 docs/superpowers/specs/2026-08-04-welle3-thema12-archiv-zip-design.md):
                 vierter Eintrag (Archiv) — kein direkter Download, sondern ein
                 Auswahl-Modal (`visitInModal`), darum ein Button statt `jet-dropdown-link
                 as="a"`. -->
            <jet-dropdown
                v-if="
                    deadlinesExportLink ||
                    buyersExportLink ||
                    budgetExportLink ||
                    bkpModalLink ||
                    archiveModalLink
                "
                align="right"
                class="ml-6"
            >
                <template #trigger>
                    <button type="button" class="flex items-center text-sm text-gray-500">
                        <DownloadIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                        <span class="hidden md:flex ml-1">
                            {{ $t("exports.menu") }}
                        </span>
                    </button>
                </template>

                <template #content>
                    <jet-dropdown-link
                        v-if="deadlinesExportLink"
                        as="a"
                        :href="deadlinesExportLink"
                    >
                        {{ $t("exports.deadlines") }}
                    </jet-dropdown-link>
                    <jet-dropdown-link
                        v-if="buyersExportLink"
                        as="a"
                        :href="buyersExportLink"
                    >
                        {{ $t("exports.buyers") }}
                    </jet-dropdown-link>
                    <jet-dropdown-link
                        v-if="budgetExportLink"
                        as="a"
                        :href="budgetExportLink"
                    >
                        {{ $t("exports.budget") }}
                    </jet-dropdown-link>
                    <!-- BKP-Export (Spec `product/backlog/bkp-auswertung-export.md`):
                         Auswahlfenster wie beim Archiv, deshalb ein Button und
                         kein direkter Download-Link. -->
                    <button
                        v-if="bkpModalLink"
                        type="button"
                        class="block w-full px-4 py-2 text-sm leading-5 text-gray-700 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                        @click="openBkpModal"
                    >
                        {{ $t("exports.bkp") }}
                    </button>
                    <button
                        v-if="archiveModalLink"
                        type="button"
                        class="block w-full px-4 py-2 text-sm leading-5 text-gray-700 text-left hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition"
                        @click="openArchiveModal"
                    >
                        {{ $t("exports.archive") }}
                    </button>
                </template>
            </jet-dropdown>

            <!-- Welle 4, Thema 8 (Spec
                 docs/superpowers/plans/2026-08-05-welle4-thema8-dokumente-verteilen.md):
                 eigener Button — NICHT im Export-Dropdown (das ist ein
                 Download-Menü, dies hier ist eine Mutation). -->
            <button
                v-if="distributeDocumentsLink"
                type="button"
                class="flex items-center text-sm text-gray-500 ml-6"
                @click="openDistributeDocumentsModal"
            >
                <DocumentAddIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                <span class="hidden md:flex ml-1">
                    {{ $t("documents.distribute") }}
                </span>
            </button>

            <!-- Thema 1 (Spec docs/superpowers/specs/2026-08-04-welle2-spalten-sichtbarkeit-design.md):
                 Projekt-Einstieg der Spalten-Sichtbarkeit (Entscheid Luca 3). -->
            <button
                v-if="columnsSettingsLink"
                type="button"
                class="flex items-center text-sm text-gray-500 ml-6"
                @click="editColumns"
            >
                <TableIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                <span class="hidden md:flex ml-1">
                    {{ $t("columns.manage") }}
                </span>
            </button>

            <!-- V2-04: "Vorlage" steht ausdrücklich ganz aussen rechts. -->
            <Link
                v-if="manageTopicsRoute"
                :href="route(manageTopicsRoute, { project: project.uuid })"
                class="flex items-center text-sm text-gray-500 ml-6"
            >
                <CogIcon class="mr-2 h-5 w-5 text-minplan-darkgreybeige" />
                <span class="hidden md:flex ml-1">
                    {{ $t("projects.template") }}
                </span>
            </Link>
        </div>
    </div>
</template>

<script>
import {
    CogIcon,
    DocumentAddIcon,
    DownloadIcon,
    ExclamationIcon,
    FolderIcon,
    LocationMarkerIcon,
    TableIcon,
    TagIcon,
    TrashIcon,
} from "@heroicons/vue/outline";
import { Link } from "@inertiajs/inertia-vue3";
import JetDropdown from "@/Jetstream/Dropdown.vue";
import JetDropdownLink from "@/Jetstream/DropdownLink.vue";

export default {
    props: [
        "project",
        "apartments",
        "missedDeadlines",
        "deleteProjectRoute",
        "editProjectStateRoute",
        "manageTopicsRoute",
        "columnsSettingsLink",
        "deletable",
        "deadlinesExportLink",
        "buyersExportLink",
        "budgetExportLink",
        "bkpModalLink",
        "archiveModalLink",
        "distributeDocumentsLink",
    ],
    components: {
        TagIcon,
        ExclamationIcon,
        TrashIcon,
        CogIcon,
        TableIcon,
        DocumentAddIcon,
        DownloadIcon,
        FolderIcon,
        LocationMarkerIcon,
        Link,
        JetDropdown,
        JetDropdownLink,
    },
    methods: {
        deleteProject() {
            this.$inertia.visitInModal(this.deleteProjectRoute);
        },
        editProjectState() {
            this.$inertia.visitInModal(this.editProjectStateRoute);
        },
        editColumns() {
            this.$inertia.visitInModal(this.columnsSettingsLink);
        },
        openBkpModal() {
            this.$inertia.visitInModal(this.bkpModalLink);
        },
        openArchiveModal() {
            this.$inertia.visitInModal(this.archiveModalLink);
        },
        openDistributeDocumentsModal() {
            this.$inertia.visitInModal(this.distributeDocumentsLink);
        },
    },
    computed: {
        mapsLink() {
            let maps = "https://www.google.com/maps/search/?api=1&query=";
            let address = encodeURIComponent(this.project.street);
            let zip = encodeURIComponent(this.project.zip);
            let city = encodeURIComponent(this.project.city);

            return maps + zip + "+" + city + "%2C" + address;
        },
    },
};
</script>

<style scoped></style>
